import { ArrowUpCircle, CheckCircle2, Eye, History } from "lucide-react";
import { Link } from "react-router";

import type { PatientResponseDto } from "~/lib/api";

import { PRIORITY_META } from "./priority";

/** Pending tasks = those not yet completed (completed_at IS NULL). */
function pendingTaskCount(patient: PatientResponseDto): number {
  return patient.tasks.filter((task) => task.completedAt === null).length;
}

/** Any still-open task carried over from the previous shift (HAN-82). */
function hasCarriedOverTask(patient: PatientResponseDto): boolean {
  return patient.tasks.some((task) => task.completedAt === null && task.carriedFromTaskId !== null);
}

function taskCountLabel(count: number): string {
  if (count === 0) return "No tasks";
  return `${count} task${count === 1 ? "" : "s"}`;
}

/**
 * One patient in the list (HAN-47): the whole card is a link to the patient's
 * detail screen. Shows the name, bed + hospital number, a priority pill matching
 * the section colour, the pending-tasks count, an optional one-line alert in red,
 * and a green acknowledged badge once the incoming doctor has acknowledged.
 */
export function PatientCard({
  sessionId,
  patient,
}: {
  sessionId: string;
  patient: PatientResponseDto;
}): JSX.Element {
  const meta = PRIORITY_META[patient.priority];
  const pending = pendingTaskCount(patient);
  const carriedOver = hasCarriedOverTask(patient) && !patient.acknowledgedAt;

  return (
    <Link
      to={`/session/${sessionId}/patient/${patient.id}`}
      className="block rounded-xl border bg-card p-4 text-card-foreground shadow-sm transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-lg font-semibold tracking-tight">{patient.fullName}</span>
            <span className="text-sm text-muted-foreground">
              <span>{patient.bedNumber}</span>
              <span aria-hidden="true"> · </span>
              <span>{patient.hospitalNumber}</span>
            </span>
          </div>
          {patient.alert ? (
            <p className="mt-1 truncate text-sm font-medium text-priority-high">{patient.alert}</p>
          ) : null}
        </div>
        <span
          className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${meta.pillClass}`}
        >
          {meta.label}
        </span>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
        <span>{taskCountLabel(pending)}</span>
        {carriedOver ? (
          // HAN-82: the patient carries unfinished work from the previous shift.
          // Indigo to match the carried-over banner/badge and read distinctly
          // from the amber escalated and green acknowledged states.
          <span
            className="inline-flex items-center gap-1 font-medium text-indigo-600"
            data-testid="patient-carried-over-indicator"
          >
            <History className="h-4 w-4" aria-hidden="true" />
            Carried over
          </span>
        ) : null}
        {patient.viewed ? (
          // HAN-81: a subtle marker that the incoming doctor has opened this
          // patient — the "no patient skipped" cue. Icon-only and muted so it
          // never competes with the priority/acknowledged/escalated states;
          // the aria-label carries the meaning for assistive tech.
          <span
            className="inline-flex items-center"
            role="img"
            aria-label="Viewed"
            data-testid="patient-viewed-indicator"
          >
            <Eye className="h-4 w-4" aria-hidden="true" />
          </span>
        ) : null}
        {patient.acknowledgedAt ? (
          <span className="inline-flex items-center gap-1 font-medium text-priority-low">
            <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
            Acknowledged
          </span>
        ) : null}
        {patient.escalatedAt && !patient.acknowledgedAt ? (
          // HAN-80: the escalated indicator on the patient in the list. Dropped
          // once acknowledged — the patient is signed off and no longer actionable.
          <span
            className="inline-flex items-center gap-1 font-medium text-amber-600"
            data-testid="patient-escalated-indicator"
          >
            <ArrowUpCircle className="h-4 w-4" aria-hidden="true" />
            Escalated
          </span>
        ) : null}
        {patient.archivedAt ? (
          // Task 18: subtle badge for completed/archived patients shown in the
          // Completed tab. Muted so it doesn't compete with the priority pill or
          // other status indicators.
          <span
            className="inline-flex items-center font-medium text-muted-foreground"
            data-testid="patient-completed-badge"
          >
            Completed
          </span>
        ) : null}
      </div>
    </Link>
  );
}
